import React, { useEffect, useState } from 'react';
import {
	merge, Subject, takeUntil, tap,
} from 'rxjs';
import loadable, { LoadableLibrary } from '@loadable/component';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Container } from '../../../../../symphony';

import { SlDataService } from '../../../services/sl-data.service';
import { SlLEContext } from '../../../global-constants';
import { disableBodyScroll, enableBodyScroll } from '../../../utils/helpers';
import {
	ActionType,
	ILpComments,
	RequestType,
} from '../../../global-interface';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import { SlExternalWindowPanel } from '../../../../../shared/public/SlExternalWindowPanel/SlExternalWindowPanel';

import s from './LPComments.scss';

const LPCommentsLoadable: LoadableLibrary<unknown> = loadable.lib(
	() => import(/* webpackPrefetch: true */ '../../../../../comments/public/SlLPComments/SlLPComments'),
);

export function LPComments(): JSX.Element {
	useStyles(s);

	const [isShown, setIsShown] = useState<boolean>(null);
	const [isOpen, setIsOpen] = useState<boolean>(null);
	const [comments, setComments] = useState<ILpComments>(null);

	const {
		isShownLPComments$, lessonPageIndex$, lesson$, lpComments$,
	} = Container.take(SlLEContext, SlDataService);

	const currentMaterial = lesson$.value.children[lessonPageIndex$.value - 1].materialInfo;
	useEffect(() => {
		const unsubscriber = new Subject<void>();

		merge(
			isShownLPComments$.pipe(tap(setIsShown)),
			lpComments$.pipe(tap(setComments)),
		)
			.pipe(takeUntil(unsubscriber))
			.subscribe();

		return () => {
			isShownLPComments$.next(false);
			enableBodyScroll();
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	useEffect(() => {
		if (isShown) {
			setIsOpen(true);
			disableBodyScroll();
		} else {
			enableBodyScroll();
		}
	}, [isShown]);

	const handleClosePanel = () => {
		const { actions$ } = Container.take(SlLEContext, SlActions);

		setTimeout(() => {
			actions$.next({
				type: ActionType.OnCloseLPCommentsClick,
				requestType: RequestType.Parallel,
			});
		}, 500);

		setIsOpen(false);
	};

	const handleUpdateCommentsCount = (count: number) => {
		const { actions$ } = Container.take(SlLEContext, SlActions);

		actions$.next({
			type: ActionType.OnUpdateLPCommentsCB,
			requestType: RequestType.Parallel,
			data: {
				lpCommentsCount: count,
			},
		});
	};

	return (
		isShown && (
			<div className="sl-le-lp-comments">
				<SlExternalWindowPanel
					isPanelOpen={isOpen}
					onClose={handleClosePanel}
					shouldAnimate
				>
					<LPCommentsLoadable>
						{({ SlLPComments }) => (
							<SlLPComments
								hideComments={handleClosePanel}
								commentContainerId={
									currentMaterial.commentContainerId
								}
								commentsCount={comments.count}
								updateCommentsCount={
									handleUpdateCommentsCount
								}
								materialHeader={
									currentMaterial.header
								}
							/>
						)}
					</LPCommentsLoadable>
				</SlExternalWindowPanel>
			</div>
		)
	);
}
